// Phase SIKA-4a — N05 enforcement: detect same-candidate overlap across goals.
//
// Source: SIKA_Independence_Architecture_v1.0 §11 N05 + §07 portfolio doctrine.
//
// Doctrine (N05 — never silently merge or share):
//   - Pure function. No DB calls, no state.
//   - Returns overlaps as { fixture_id, market, pick, goal_names, leg_ids }.
//   - The UI renders the verbatim Table 18-style doctrinal copy via
//     `formatSameCandidateBanner` — promotion-not-restriction.
//   - Two legs collide when fixture_id + market + pick are all identical
//     and non-null/non-empty. If any of those three are missing the leg
//     is excluded from collision detection (we can't safely call it the
//     same candidate without all three).

export interface SameCandidateLeg {
  leg_id: string;
  goal_id: string;
  goal_name: string;
  fixture_id: string | null;
  market: string | null;
  pick: string | null;
}

export interface SameCandidateFlag {
  fixture_id: string;
  market: string;
  pick: string;
  goal_names: string[];
  leg_ids: string[];
}

function legKey(leg: SameCandidateLeg): string | null {
  const fx = leg.fixture_id;
  const mk = leg.market;
  const pk = leg.pick;
  if (!fx || !mk || !pk) return null;
  return `${fx}::${mk}::${pk}`;
}

/** Pure: find every (fixture_id, market, pick) selected by 2+ distinct goals. */
export function findSameCandidateOverlap(
  legs: SameCandidateLeg[],
): SameCandidateFlag[] {
  // Group legs by key. Track set of distinct goal_ids per key.
  const groups = new Map<
    string,
    { fixture_id: string; market: string; pick: string; legs: SameCandidateLeg[] }
  >();
  for (const leg of legs) {
    const key = legKey(leg);
    if (key === null) continue;
    const existing = groups.get(key);
    if (existing) {
      existing.legs.push(leg);
    } else {
      groups.set(key, {
        fixture_id: leg.fixture_id as string,
        market: leg.market as string,
        pick: leg.pick as string,
        legs: [leg],
      });
    }
  }

  const flags: SameCandidateFlag[] = [];
  for (const grp of groups.values()) {
    const distinctGoals = new Set(grp.legs.map((l) => l.goal_id));
    if (distinctGoals.size < 2) continue;
    // Preserve insertion-ordered goal names (deduped).
    const seen = new Set<string>();
    const goal_names: string[] = [];
    const leg_ids: string[] = [];
    for (const l of grp.legs) {
      leg_ids.push(l.leg_id);
      if (!seen.has(l.goal_id)) {
        seen.add(l.goal_id);
        goal_names.push(l.goal_name);
      }
    }
    flags.push({
      fixture_id: grp.fixture_id,
      market: grp.market,
      pick: grp.pick,
      goal_names,
      leg_ids,
    });
  }
  return flags;
}

/** Doctrinal banner copy. Promotion-not-restriction. */
export function formatSameCandidateBanner(flag: SameCandidateFlag): string {
  const [a, b, ...rest] = flag.goal_names;
  const others = rest.length > 0 ? ` and ${String(rest.length)} other` : "";
  return (
    `Both your ${a ?? "first"} and ${b ?? "second"}${others} plans have ` +
    `selected ${flag.pick} (${flag.market}) this week. ` +
    `Consider whether you want double exposure.`
  );
}
